import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  BarChart3, Timer, Clock, User, Building2, ChevronLeft, ChevronRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatResponseTime(seconds) {
  if (!seconds && seconds !== 0) return '--';
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  if (mins > 0) return `${mins}m ${secs}s`;
  return `${secs}s`;
}

function formatTime(isoString) {
  if (!isoString) return '--';
  return new Date(isoString).toLocaleString();
}

export default function ReportingPage() {
  const [data, setData] = useState(null);
  const [dealerships, setDealerships] = useState([]);
  const [selectedDealership, setSelectedDealership] = useState('all');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const LIMIT = 25;
  const { token } = useAuth();
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    const fetchDealerships = async () => {
      try {
        const res = await axios.get(`${API}/dealerships`, { headers, withCredentials: true });
        setDealerships(res.data);
      } catch (err) { console.error('Failed to fetch dealerships:', err); }
    };
    fetchDealerships();
  }, []);

  useEffect(() => {
    const fetchReport = async () => {
      setLoading(true);
      try {
        const params = { limit: LIMIT, skip: page * LIMIT };
        if (selectedDealership && selectedDealership !== 'all') {
          params.dealership_id = selectedDealership;
        }
        const res = await axios.get(`${API}/reporting`, { headers, params, withCredentials: true });
        setData(res.data);
      } catch (err) { console.error('Failed to fetch report:', err); }
      setLoading(false);
    };
    fetchReport();
  }, [selectedDealership, page]);

  const totalPages = data ? Math.ceil(data.total / LIMIT) : 0;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="reporting-page">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white uppercase tracking-tight">
            Reporting
          </h1>
          <p className="text-slate-400 text-sm mt-2">Response time tracking per lead</p>
        </div>
        <Select value={selectedDealership} onValueChange={v => { setSelectedDealership(v); setPage(0); }}>
          <SelectTrigger className="w-[220px] bg-[#111] border-[#333] h-10" data-testid="report-dealership-filter">
            <SelectValue placeholder="All Dealerships" />
          </SelectTrigger>
          <SelectContent className="bg-[#171717] border-[#262626]">
            <SelectItem value="all">All Dealerships</SelectItem>
            {dealerships.map(d => (
              <SelectItem key={d.dealership_id} value={d.dealership_id}>{d.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary stats */}
      {data && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-8">
          <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="report-total">
            <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Total Leads Processed</p>
            <p className="font-heading text-4xl font-bold text-white mt-2">{data.total}</p>
          </Card>
          <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="report-responded">
            <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Responses Generated</p>
            <p className="font-heading text-4xl font-bold text-white mt-2">{data.total_responded}</p>
          </Card>
          <Card className="bg-[#0A0A0A] border-[#262626] p-6" data-testid="report-avg-time">
            <p className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Average Response Time</p>
            <p className="font-heading text-4xl font-bold text-red-400 mt-2">{formatResponseTime(data.avg_response_time_seconds)}</p>
          </Card>
        </div>
      )}

      {/* Report table */}
      <Card className="bg-[#0A0A0A] border-[#262626] overflow-hidden" data-testid="report-table">
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : !data || data.leads.length === 0 ? (
          <div className="p-12 text-center">
            <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-600" />
            <h3 className="font-heading text-xl font-semibold text-white uppercase mb-2">No Report Data</h3>
            <p className="text-slate-500 text-sm">Process some leads to see reporting data here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-[#262626] hover:bg-transparent">
                  <TableHead className="text-[10px] font-bold tracking-widest text-slate-500 uppercase h-12">Customer Name</TableHead>
                  <TableHead className="text-[10px] font-bold tracking-widest text-slate-500 uppercase h-12">Dealership</TableHead>
                  <TableHead className="text-[10px] font-bold tracking-widest text-slate-500 uppercase h-12">Lead Type</TableHead>
                  <TableHead className="text-[10px] font-bold tracking-widest text-slate-500 uppercase h-12">Time Received</TableHead>
                  <TableHead className="text-[10px] font-bold tracking-widest text-slate-500 uppercase h-12">Time Responded</TableHead>
                  <TableHead className="text-[10px] font-bold tracking-widest text-slate-500 uppercase h-12 text-right">Response Time</TableHead>
                  <TableHead className="text-[10px] font-bold tracking-widest text-slate-500 uppercase h-12 text-right">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.leads.map((lead, i) => (
                  <TableRow
                    key={lead.lead_id}
                    className="border-b border-[#1A1A1A] hover:bg-[#0F0F0F] transition-colors"
                    data-testid={`report-row-${lead.lead_id}`}
                  >
                    <TableCell className="font-medium text-white text-sm py-4">
                      <div>
                        <p>{lead.customer_name || 'Unknown'}</p>
                        <p className="text-[10px] text-slate-500 mt-0.5">{lead.customer_email || lead.sender_email}</p>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-slate-300">{lead.dealership_name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[9px] font-bold tracking-wider border-[#333] text-slate-400 uppercase">
                        {lead.lead_type_label || lead.lead_type}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-slate-400 font-mono">{formatTime(lead.received_at)}</TableCell>
                    <TableCell className="text-xs text-slate-400 font-mono">{formatTime(lead.responded_at)}</TableCell>
                    <TableCell className="text-right">
                      <span className="font-heading text-sm font-bold text-red-400">
                        {formatResponseTime(lead.response_time_seconds)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Badge className={`text-[9px] font-bold tracking-wider uppercase border ${
                        lead.response_status === 'sent' ? 'bg-green-500/10 text-green-400 border-green-500/20' :
                        lead.response_status === 'pending_review' ? 'bg-orange-500/10 text-orange-400 border-orange-500/20' :
                        'bg-red-500/10 text-red-400 border-red-500/20'
                      }`}>
                        {lead.response_status === 'pending_review' ? 'Pending' : lead.response_status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-6">
          <Button variant="ghost" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="text-slate-400 hover:text-white" data-testid="report-prev-btn">
            <ChevronLeft className="w-4 h-4 mr-1" /> Previous
          </Button>
          <span className="text-xs text-slate-500 font-bold tracking-wider">Page {page + 1} of {totalPages}</span>
          <Button variant="ghost" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} className="text-slate-400 hover:text-white" data-testid="report-next-btn">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      )}
    </div>
  );
}
